/*
 * File: utilities.js
 * Project: our-wave-stories-app
 *
 * Created by Brendan Michaelsen on December 30, 2021 at 4:28 PM
 * Copyright © 2021 - 2024 Our Wave, Inc. All rights reserved.
 *
 * Last Modified: June 9, 2025 at 10:06 PM
 * Modified By: Brendan Michaelsen
 */

/**
 * Imports
 */

// Constants
import {
	AVAILABLE_LOCALES,
	OBFUSCATE_REGEX,
	OBFUSCATE_TEXT_REPLACE,
	FULL_STOP_PUNCTUATION
} from '../constants/base.constants';


/**
 * Utilities
 */

export const formatTextRedactions = (content = '') => content.replace(OBFUSCATE_REGEX, (str) => {
	const element = str.slice(2, -2);
	if (element === '') {
		return `(${OBFUSCATE_TEXT_REPLACE})`;
	}
	return `(${element})`;
});

export const generateSlugFromContent = (content) => {
	const a = 'àáäâãåăæąçćčđďèéěėëêęğǵḧìíïîįłḿǹńňñòóöôœøṕŕřßşśšșťțùúüûǘůűūųẃẍÿýźžż·/_,:;';
	const b = 'aaaaaaaaacccddeeeeeeegghiiiiilmnnnnooooooprrsssssttuuuuuuuuuwxyyzzz------';
	const p = new RegExp(a.split('').join('|'), 'g');

	// Build slug
	let slug = content
		.toString()
		.toLowerCase()
		.replace(/\s+/g, '-') // Replace spaces with -
		.replace(p, (c) => b.charAt(a.indexOf(c))) // Replace special characters
		.replace(/&/g, '-and-') // Replace & with 'and'
		.replace(/[^\p{L}\p{N}-]/gu, '') // Remove all non-word characters
		.replace(/--+/g, '-') // Replace multiple - with single -
		.replace(/^-+/, '') // Trim - from start of text
		.replace(/-+$/, ''); // Trim - from end of text

	// Limit length of slug
	if (slug.length > 200) slug = slug.substring(0, 200).replace(/-+$/, '');

	// Return slug
	return slug;
};

export const generateReferenceIdFromContent = (content) => {

	// Generate slug from content
	const referenceId = generateSlugFromContent(content);

	// Replace hyphens with underscores
	return referenceId.replace(/-/g, '_');
};

export const isSlugOrId = (text) => !(text.search(/^[\p{L}\p{N}\-_()]+$/gu) === -1);

export const stripHtml = (text) => text.replace(/<\/?[^>]+(>|$)/g, '').replace(/&nbsp;/g, '');

export const isValueInObject = (value, obj) => {
	const values = Object.values(obj);
	for (let i = 0; i < values.length; i += 1) {
		if (values[i] === value) {
			return true;
		}
	}
	return undefined;
};

export const formatNumber = (n) => {
	if (n < 1e3) return n.toFixed(0);
	if (n >= 1e3 && n < 1e6) return `${+(n / 1e3).toFixed(1)}K`;
	if (n >= 1e6 && n < 1e9) return `${+(n / 1e6).toFixed(1)}M`;
	if (n >= 1e9 && n < 1e12) return `${+(n / 1e9).toFixed(1)}B`;
	if (n >= 1e12) return `${+(n / 1e12).toFixed(1)}T`;
	return null;
};

export const formatNumberWithCommas = (n) => n.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');

export const arrayContainsAnotherArray = (needle, haystack) => {
	for (let i = 0; i < needle.length; i += 1) {
		if (haystack.indexOf(needle[i]) === -1) return false;
	}
	return true;
};

export const getAllIndexes = (arr, val) => {
	const indexes = [];
	let i = -1;
	while ((i = arr.indexOf(val, i + 1)) !== -1) { /* eslint-disable-line no-cond-assign */
		indexes.push(i);
	}
	return indexes;
};

export const getRandomArrayValues = (arr, n) => {
	const result = new Array(n);
	let len = arr.length;
	const taken = new Array(len);
	if (n > len) { return n; }
	// eslint-disable-next-line no-plusplus, no-param-reassign
	while (n--) {
		const x = Math.floor(Math.random() * len);
		result[n] = arr[x in taken ? taken[x] : x];
		// eslint-disable-next-line no-plusplus
		taken[x] = --len in taken ? taken[len] : len;
	}
	return result;
};

export const parseFirstLastName = (name) => {
	if (!name) return {};
	const nameArray = name.split(/\s+/);
	const firstName = nameArray[0].trim();
	const lastName = name.replace(firstName, '').trim();
	return { firstName, lastName };
};

export const reduceToBaseURL = (p) => {
	let path = p;
	if (path) {
		AVAILABLE_LOCALES.forEach((locale) => {
			const testArray = path.split(locale);
			if (testArray.length > 1 && testArray[0] === '/') {
				const testPath = testArray[1];
				if ((testPath.length === 0 || testPath.charAt(0) === '/' || testPath.charAt(0) === '?')) {
					path = path.replace(locale, '').replace('//', '/');
				}
			}
		});
	}
	return path;
};

export const splitSentences = (content) => {

	// Create regex
	const regex = new RegExp(`[^${FULL_STOP_PUNCTUATION.join('')}]+[${FULL_STOP_PUNCTUATION.join('')}]+`, 'g');

	// Find matches
	const matches = content.match(regex);

	// Return sentences
	return matches ? matches.map((m) => m.trim()) : [content];
};

export const chunkString = (content, lengthLimit) => {

	// Check if chunking is needed
	if (content <= lengthLimit) {
		return [content];
	}

	// Split sentences
	const sentences = splitSentences(content);

	// Initialize chunks
	let chunks = [''];

	// Create chunks
	let chunkIndex = 0;
	sentences.forEach((sentence) => {
		if ((chunks[chunkIndex].length + sentence.length + 1) <= lengthLimit) {
			chunks[chunkIndex] += ` ${sentence}`;
		} else {
			chunks.push(sentence);
			chunkIndex += 1;
		}
	});

	// Clean chunks
	chunks = chunks.filter(Boolean).map((c) => c.trim());

	// Return chunks
	return chunks.length > 0 ? chunks : [content];
};

export const containsUrl = (content, whitelistDomains = []) => {
	const urlRegex = /((https?|ftp|file):\/\/[-A-Z0-9+&@#/%?=~_|!:,.;]*[-A-Z0-9+&@#/%=~_|])/ig;
	const matches = content.replace(/\s/g, '').match(urlRegex) || [];
	return matches.length > 0 ? matches.every((match) => {
		const components = match.split('://');
		if (components.length > 0) {
			const [domain] = components[1].split('/');
			return !whitelistDomains.includes(domain.toLowerCase());
		}
		return true;
	}) : false;
};

// eslint-disable-next-line no-promise-executor-return
export const timeout = (ms) => new Promise((res) => setTimeout(res, ms));

export const maskEmail = (email) => {
	const [localPart, domain] = email.split('@');
	if (!localPart || !domain) return email;
	const visibleChars = Math.min(3, localPart.length);
	const maskedPart = '*'.repeat(localPart.length - visibleChars);
	const maskedEmail = `${localPart.slice(0, visibleChars)}${maskedPart}@${domain}`;
	return maskedEmail;
};

export const truncateString = (str, n, useWordBoundary = true) => {
	if (str.length <= n) { return str; }
	const subString = str.slice(0, n - 1);
	return `${useWordBoundary
		? subString.slice(0, subString.lastIndexOf(' '))
		: subString}...`;
};

export const randomNumberInRange = (min, max) => Math.floor(Math.random() * (max - min + 1)) + min;
